/**
 * 86exwdkr5 (PR-1) - Service worker registration + update flow.
 *
 * Imported from `main.tsx` on app boot. Registers the SW only when:
 *   - We're running in production (`import.meta.env.PROD`), AND
 *   - We're the host app (`__APP_TYPE__ === 'host'`).
 *
 * The admin console never registers a SW - it's desktop-only and would
 * otherwise pollute IndexedDB / CacheStorage with a separate app's
 * assets if a user has both open in the same browser.
 *
 * When a new SW is waiting, we surface an `'sw:update-available'`
 * `CustomEvent` on `window` so the network-status pill can render a
 * "New version available - refresh?" CTA without coupling this module
 * to React.
 */

import { Workbox } from 'workbox-window'
import { recordInstall, recordPersistStatus } from './db'
import { browserReloadDeps, reloadWhenIdle } from './safeReload'
import {
  claimReloadSlot,
  controllingAction,
  SKIP_WAITING_IF_ALL_HIDDEN,
  shouldCheckForUpdate,
  type SlotStorage,
} from './swUpdate'
import { toast } from '@/shared/lib/toast'

/** Reload now, or once the user closes the dialog / leaves the field they are in. */
export function reloadAppSafely(): void {
  reloadWhenIdle(browserReloadDeps((msg) => toast.info(msg, { duration: 15_000 })))
}

let _wb: Workbox | null = null
// Set when THIS tab's user pressed Refresh on the update prompt: their own
// request reloads at once, an update accepted in another tab waits.
let _acceptedHere = false
// True once a new worker is installed and waiting. The React provider reads
// it on mount: 'waiting' can fire before the provider's listener exists, and
// a missed event meant no prompt for the whole session.
let _updateWaiting = false
// True when a new worker took control after an update accepted elsewhere
// while this tab was visible: Reload then just reloads.
let _activatedElsewhere = false

/** Ask the waiting worker to activate if no window of the app is visible.
 *  Only a hidden tab asks; the worker checks every tab before acting. */
function _requestAutoApply(): void {
  if (!_wb || !_updateWaiting || _acceptedHere || document.visibilityState !== 'hidden') return
  void _wb.messageSW({ type: SKIP_WAITING_IF_ALL_HIDDEN }).catch(() => undefined)
}

/** True when a new service worker is installed and waiting to take over. */
export function isUpdateWaiting(): boolean {
  return _updateWaiting
}

// N3 loop guard: at most one AUTOMATIC SW reload per window per tab. A
// reload the user clicked for is never guarded.
const RELOAD_GUARD_WINDOW_MS = 10_000
// If controllerchange has not arrived this long after skipWaiting (the
// waiting worker vanished, or another tab already activated it), reload
// anyway: the network-first shell then loads the new build regardless.
const SKIP_WAITING_FALLBACK_MS = 4_000

function _sessionStore(): SlotStorage | null {
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

let _fallbackTimer: number | null = null

/** True when an automatic SW reload is allowed (loop guard). */
function _claimAutoReload(): boolean {
  return claimReloadSlot(_sessionStore(), Date.now(), RELOAD_GUARD_WINDOW_MS)
}

/**
 * Reload the user explicitly asked for. NOT loop-guarded: one click is one
 * reload. Guarding it let an earlier fallback reload use the slot, so a second
 * click spun forever with the prompt never clearing.
 */
function _userReload(): void {
  if (_fallbackTimer != null) {
    window.clearTimeout(_fallbackTimer)
    _fallbackTimer = null
  }
  window.location.reload()
}

export function registerServiceWorker(): void {
  if (typeof window === 'undefined') return
  if (!('serviceWorker' in navigator)) return
  // Vite injects __APP_TYPE__ at compile time.
  if (__APP_TYPE__ !== 'host') return
  // Skip in dev - the dev SW would hot-reload assets in confusing ways.
  if (!import.meta.env.PROD) return

  try {
    // MUST match the `filename` the injectManifest build emits (see
    // vite.config.ts VitePWA `filename: 'service-worker.ts'` -> built as
    // `/service-worker.js`). This was `/sw.js` - the generateSW default - left
    // over from before the switch to a custom SW, so the browser fetched a
    // non-existent path, got the SPA index.html (text/html) back, and failed to
    // install it as a worker: "sw.js ... encountered an error during installation".
    _wb = new Workbox('/service-worker.js')

    _wb.addEventListener('waiting', () => {
      _updateWaiting = true
      // Tell the React layer a new version is ready. The host app
      // listens for this event in NetworkStatusProvider and surfaces
      // the prompt in the topbar.
      window.dispatchEvent(new CustomEvent('sw:update-available'))
      // A background tab asks the worker to take over; the worker only does
      // so when EVERY window of the app is hidden (skipWaiting activates it
      // for all tabs, and a visible one may be mid-sale).
      _requestAutoApply()
    })
    // A tab that was visible when the update arrived asks again once the
    // user switches away, if nobody pressed Reload.
    document.addEventListener('visibilitychange', _requestAutoApply)

    _wb.addEventListener('controlling', (event) => {
      // A new SW took over after an accepted update prompt: reload so the
      // running JS matches the precached assets the new worker serves.
      //
      // Gated on `isUpdate`: on the FIRST-ever install (fresh browser, or
      // site data cleared - i.e. every offline-testing session), the
      // worker's clientsClaim() claims the open page and this event fires
      // with isUpdate falsy. Reloading then yanked the page out from under
      // whatever the operator was doing - the booking modal "reloading"
      // mid-entry was this. On first install the running JS and the
      // freshly-written precache are the same deploy, so there is nothing
      // to resync and no reason to reload.
      //
      // FO-20: an update accepted in ANOTHER tab also lands here, in every
      // open tab, so the reload waits until this tab has no dialog open and
      // no field in focus instead of discarding a half-typed payment.
      const visible = document.visibilityState === 'visible'
      const action = controllingAction({
        isUpdate: !!event.isUpdate,
        acceptedHere: _acceptedHere,
        visible,
        // Only consulted (and only consumes the slot) for automatic reloads.
        guardOk: !_acceptedHere && !visible && !!event.isUpdate && _claimAutoReload(),
      })
      if (action === 'none') return
      if (action === 'prompt') {
        // Another tab's update reached this visible tab. Never reload under
        // the user: keep (or raise) the Reload prompt. Its button reloads
        // straight away, since the new worker is already in control.
        _activatedElsewhere = true
        _updateWaiting = true
        window.dispatchEvent(new CustomEvent('sw:update-available'))
        return
      }
      _updateWaiting = false
      // The new worker is in control: clear the prompt and its spinner.
      window.dispatchEvent(new CustomEvent('sw:update-applied'))
      if (action === 'reload-now') _userReload()
      else if (action === 'reload-when-idle') reloadAppSafely()
    })

    _wb.register().then(() => {
      // First-run install - record and request persistent storage so
      // Safari's 7-day eviction is less likely to wipe the cache on a
      // tablet that sits idle over a weekend.
      void recordInstall()
      void requestPersistentStorage()
      startUpdatePolling()
    })
  } catch (err) {
    if (typeof console !== 'undefined') {
      console.warn('[pwa] SW registration failed', err)
    }
  }
}

// The browser only re-checks a registered SW script on its own ~24h cadence
// (or a navigation), so a front-desk tab left open learned about a deploy
// late (it was an hourly poll; N3 found staff still on the old build after a
// staging deploy). Poll every 60 s while the tab is visible and online, and
// also on visibilitychange (back from another tab) and window focus. The
// shared throttle makes a focus + visibility + tick burst cost one request.
// update() failures (offline) are irrelevant; the next tick retries.
const UPDATE_CHECK_INTERVAL_MS = 60 * 1000
const UPDATE_CHECK_MIN_GAP_MS = 30 * 1000
let _lastUpdateCheckAt = 0

/** Ask the browser to re-fetch the SW script, throttled; safe to call often. */
export function checkForServiceWorkerUpdate(): void {
  if (!_wb) return
  const now = Date.now()
  const go = shouldCheckForUpdate({
    hidden: document.hidden,
    online: navigator.onLine !== false,
    now,
    lastCheckAt: _lastUpdateCheckAt,
    minGapMs: UPDATE_CHECK_MIN_GAP_MS,
  })
  if (!go) return
  _lastUpdateCheckAt = now
  void _wb.update()?.catch(() => {
    /* offline or transient - next tick retries */
  })
}

function startUpdatePolling(): void {
  _lastUpdateCheckAt = Date.now() // register() itself just fetched the SW
  setInterval(checkForServiceWorkerUpdate, UPDATE_CHECK_INTERVAL_MS)
  document.addEventListener('visibilitychange', checkForServiceWorkerUpdate)
  window.addEventListener('focus', checkForServiceWorkerUpdate)
  // Raised by the axios client on a 400 (possible stale-build contract
  // mismatch); same throttle applies.
  window.addEventListener('sw:check-update', checkForServiceWorkerUpdate)
}

/**
 * Apply the waiting SW. Called by the React update prompt: skipWaiting, then
 * the 'controlling' handler above reloads once controllerchange fires.
 */
export function applyServiceWorkerUpdate(): void {
  if (!_wb || _activatedElsewhere) {
    _userReload()
    return
  }
  _acceptedHere = true
  // Workbox unifies the messageSW + skipWaiting handshake.
  _wb.messageSkipWaiting()
  if (_fallbackTimer != null) window.clearTimeout(_fallbackTimer)
  // User-started, so unguarded; cleared when 'controlling' arrives first.
  _fallbackTimer = window.setTimeout(_userReload, SKIP_WAITING_FALLBACK_MS)
}

/**
 * Request persistent-storage permission so the browser does not evict
 * our IDB during low-disk conditions. On iOS Safari the prompt is not
 * shown to the user - the browser decides based on interaction history;
 * but on Chrome desktop the user is asked.
 *
 * Wrapped in try/catch because the API is unavailable on some browsers
 * (older Safari, Firefox private window).
 */
async function requestPersistentStorage(): Promise<void> {
  if (typeof navigator === 'undefined' || !navigator.storage?.persist) return
  try {
    const persisted = await navigator.storage.persist()
    void recordPersistStatus(persisted)
  } catch (err) {
    if (typeof console !== 'undefined') {
      console.warn('[pwa] navigator.storage.persist failed', err)
    }
  }
}
